import { useState } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  ArrowLeft,
  Trophy,
  Eye,
  BarChart3,
  Clock,
  DollarSign,
  FileText,
  CheckCircle,
  XCircle,
  Minus,
  ChevronDown,
  ChevronUp,
  Stethoscope,
  Search,
  MessageSquare,
  Users,
  Lightbulb,
  Crown,
  Columns,
  List,
  AlertCircle,
  AlertTriangle,
  Home,
  Play,
  ClipboardCheck,
  Bot,
  Loader2,
  Sparkles,
  RefreshCw,
} from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../../lib/api';
import { format } from 'date-fns';
import { LoadingState, ErrorState, Breadcrumb } from '../../components/ui';

interface SearchExecution {
  id: string;
  searchModel: string;
  blindCode: string;
  status: string;
  searchResults?: {
    webResults?: Array<{
      name: string;
      title?: string;
      institution?: string;
      city?: string;
      canton?: string;
      specialty?: string;
      matchScore?: number;
    }>;
    searchSummary?: string;
  };
  latencyMs?: number;
  costUsd?: number;
  autoScore?: {
    relevanceScore?: number;
    geographicScore?: number;
    specialtyMatchScore?: number;
    combinedScore?: number;
  };
}

interface Execution {
  id: string;
  model: string;
  blindCode: string;
  status: string;
  modelVersion?: string;
  selectedForSearch?: boolean;
  searchExecutions?: SearchExecution[];
  indication?: {
    suitability: string;
    confidence: number;
    summary: string;
    issues: Array<{ severity: string; description: string; recommendation?: string }>;
    patientExpectations?: {
      summary: string;
      concerns: string[];
      goals: string[];
    };
    documentFindings?: {
      hasDocuments: boolean;
      hasInconsistencies: boolean;
      findings: Array<{
        documentName: string;
        observations: string[];
        clinicalSignificance?: string;
      }>;
      inconsistencies?: Array<{
        description: string;
        severity: string;
        documentSource: string;
        questionnaireSource?: string;
      }>;
      verificationStatus?: string;
    };
  };
  criteria?: {
    criteria: Array<{
      id: string;
      field: string;
      value: string | string[];
      priority: string;
      reasoning: string;
      name?: string;
      description?: string;
    }>;
    priorityExplanation?: string;
  };
  indicationLatencyMs?: number;
  criteriaLatencyMs?: number;
  indicationCostUsd?: number;
  criteriaCostUsd?: number;
  autoScore?: {
    combinedScore: number;
    suitabilityScore: number;
    confidenceScore: number;
  };
}

interface EvaluationSet {
  id: string;
  rankings: string[];
  evaluatorNotes?: string;
  evaluator: { name: string };
  createdAt: string;
  _count?: { items: number };
  items?: Array<{
    blindCode: string;
    verdict: string;
  }>;
}

interface SearchEvaluationSet {
  id: string;
  rankings: string[];
  selectedBest?: string;
  selectionReasoning?: string;
  evaluatorNotes?: string;
  evaluator: { name: string };
  createdAt: string;
  completedAt?: string;
  _count?: { items: number };
  items?: Array<{
    blindCode: string;
    verdict: string;
    scores?: Record<string, unknown>;
    searchExecutionId: string;
  }>;
}

// Grand Rounds configuration types
type LLMProvider = 'CLAUDE_OPUS_4_5' | 'CLAUDE_OPUS_4_6' | 'GPT_5_2_PRO' | 'GEMINI_3_PRO';
type GrandRoundsPromptVersion = 'v1' | 'v2';

interface GrandRoundsConfig {
  expertModels?: {
    drPrecision: LLMProvider;
    drEvidence: LLMProvider;
    drAdvocate: LLMProvider;
  };
  moderatorModel?: LLMProvider;
  promptVersion?: GrandRoundsPromptVersion;
}

// Normalize priority values from various LLM output formats to standard values
// Handles: P1_CRITICAL → critical, P2_IMPORTANT → high, P3_PREFERRED → medium, etc.
function normalizePriority(priority: string | undefined): 'critical' | 'high' | 'medium' | 'low' {
  if (!priority) return 'medium';
  const p = priority.toLowerCase();
  if (p === 'critical' || p.includes('p1') || p.includes('critical')) return 'critical';
  if (p === 'high' || p.includes('p2') || p.includes('important')) return 'high';
  if (p === 'medium' || p.includes('p3') || p.includes('preferred')) return 'medium';
  if (p === 'low' || p.includes('p4') || p.includes('optional')) return 'low';
  return 'medium'; // default fallback
}

interface TestRun {
  id: string;
  name: string;
  status: string;
  isRevealed: boolean;
  startedAt?: string;
  completedAt?: string;
  searchPhaseStatus?: 'NOT_STARTED' | 'READY' | 'RUNNING' | 'COMPLETED' | 'EVALUATED';
  config?: {
    models?: string[];
    grandRoundsConfig?: GrandRoundsConfig;
  };
  testCase: {
    id: string;
    name: string;
    code: string;
    condition: string;
  };
  executions: Execution[];
  evaluationSets: EvaluationSet[];
  searchEvaluationSets?: SearchEvaluationSet[];
}

const MODEL_DISPLAY: Record<string, { name: string; color: string }> = {
  CLAUDE_OPUS_4_5: { name: 'Claude Opus 4.5', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  CLAUDE_OPUS_4_6: { name: 'Claude Opus 4.6', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  GPT_5_2_PRO: { name: 'GPT-5.2 Pro', color: 'bg-green-100 text-green-700 border-green-200' },
  GEMINI_3_PRO: { name: 'Gemini 3 Pro', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  GRAND_ROUNDS: { name: 'Grand Rounds', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  BETTERDOC: { name: 'BetterDoc API', color: 'bg-teal-100 text-teal-700 border-teal-200' },
};

const VERDICT_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  PASS: { label: 'Pass', icon: CheckCircle, color: 'text-green-600' },
  PASS_WITH_RESERVATIONS: { label: 'Pass with Reservations', icon: CheckCircle, color: 'text-blue-600' },
  BORDERLINE: { label: 'Borderline', icon: Minus, color: 'text-amber-600' },
  FAIL_MINOR: { label: 'Fail (Minor)', icon: AlertCircle, color: 'text-orange-600' },
  FAIL_MAJOR: { label: 'Fail (Major)', icon: XCircle, color: 'text-red-600' },
  FAIL_CRITICAL: { label: 'Fail (Critical)', icon: XCircle, color: 'text-red-700' },
};

const SEARCH_VERDICT_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
  EXCELLENT: { label: 'Excellent', icon: CheckCircle, color: 'text-green-600' },
  GOOD: { label: 'Good', icon: CheckCircle, color: 'text-blue-600' },
  ADEQUATE: { label: 'Adequate', icon: Minus, color: 'text-amber-600' },
  POOR: { label: 'Poor', icon: AlertCircle, color: 'text-orange-600' },
  INADEQUATE: { label: 'Inadequate', icon: XCircle, color: 'text-red-600' },
};

const PRIORITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700 border-red-200',
  high: 'bg-orange-100 text-orange-700 border-orange-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  low: 'bg-gray-100 text-gray-600 border-gray-200',
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-gray-100 text-gray-600',
};

function ModelBadge({ model }: { model: string }) {
  const config = MODEL_DISPLAY[model] || { name: model, color: 'bg-gray-100 text-gray-700' };
  return (
    <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border', config.color)}>
      {config.name}
    </span>
  );
}

// Expert role name mapping
const EXPERT_ROLE_NAMES: Record<string, { name: string; role: string }> = {
  drPrecision: { name: 'Dr. Precision', role: 'Conservative Specialist' },
  drEvidence: { name: 'Dr. Evidence', role: 'Evidence-Based Practitioner' },
  drAdvocate: { name: 'Dr. Advocate', role: 'Patient-Centered Advocate' },
};

// Component to display Grand Rounds configuration
function GrandRoundsConfigDisplay({ config }: { config: GrandRoundsConfig }) {
  const promptVersionLabel = config.promptVersion === 'v2' ? 'Joint Evidence' : 'Democracy';
  const promptVersionColor = config.promptVersion === 'v2'
    ? 'bg-indigo-100 text-indigo-700 border-indigo-200'
    : 'bg-gray-100 text-gray-600 border-gray-200';

  return (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs">
      <div className="flex items-center gap-2 mb-2">
        <span className="font-semibold text-amber-900">Grand Rounds Configuration</span>
        <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium border', promptVersionColor)}>
          {promptVersionLabel}
        </span>
      </div>
      {config.expertModels && (
        <div className="space-y-1 text-gray-600">
          {Object.entries(config.expertModels).map(([key, model]) => {
            const expert = EXPERT_ROLE_NAMES[key];
            if (!expert) return null;
            return (
              <div key={key} className="flex items-center justify-between">
                <span>{expert.name}</span>
                <span className="text-gray-500">
                  {MODEL_DISPLAY[model]?.name || model}
                </span>
              </div>
            );
          })}
          {config.moderatorModel && (
            <div className="flex items-center justify-between pt-1 border-t border-amber-200 mt-1">
              <span>Moderator</span>
              <span className="text-gray-500">
                {MODEL_DISPLAY[config.moderatorModel]?.name || config.moderatorModel}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function VerdictDisplay({ verdict }: { verdict: string }) {
  const config = VERDICT_CONFIG[verdict] || { label: verdict, icon: Minus, color: 'text-gray-600' };
  const Icon = config.icon;
  return (
    <span className={clsx('inline-flex items-center gap-1 text-sm font-medium', config.color)}>
      <Icon className="h-4 w-4" />
      {config.label}
    </span>
  );
}

function SearchVerdictDisplay({ verdict }: { verdict: string }) {
  const config = SEARCH_VERDICT_CONFIG[verdict] || { label: verdict, icon: Minus, color: 'text-gray-600' };
  const Icon = config.icon;
  return (
    <span className={clsx('inline-flex items-center gap-1 text-sm font-medium', config.color)}>
      <Icon className="h-4 w-4" />
      {config.label}
    </span>
  );
}

function ScoreBar({ score, label }: { score: number; label: string }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-500">{label}</span>
        <span className="font-medium">{Math.round(score)}%</span>
      </div>
      <div className="mt-1 h-2 w-full rounded-full bg-gray-200">
        <div
          className={clsx(
            'h-2 rounded-full',
            score >= 80 ? 'bg-green-500' : score >= 60 ? 'bg-amber-500' : 'bg-red-500'
          )}
          style={{ width: `${score}%` }}
        />
      </div>
    </div>
  );
}

function ExecutionDetails({ execution }: { execution: Execution }) {
  const [activeTab, setActiveTab] = useState<'summary' | 'assessment' | 'expectations' | 'criteria' | 'documents'>('summary');

  const indication = execution.indication;
  const criteria = execution.criteria;
  const documentFindings = indication?.documentFindings;

  if (!indication && !criteria) {
    return (
      <div className="mt-4 p-4 bg-gray-50 rounded-lg text-center text-gray-500">
        No analysis data available
      </div>
    );
  }

  return (
    <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
      {/* Tabs */}
      <div className="flex border-b border-gray-200 bg-gray-50">
        <button
          onClick={() => setActiveTab('summary')}
          className={clsx(
            'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px',
            activeTab === 'summary'
              ? 'border-amber-500 text-amber-700 bg-white'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          <FileText className="h-4 w-4" />
          Summary
        </button>
        <button
          onClick={() => setActiveTab('assessment')}
          className={clsx(
            'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px',
            activeTab === 'assessment'
              ? 'border-amber-500 text-amber-700 bg-white'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          <Stethoscope className="h-4 w-4" />
          Medical Assessment
        </button>
        <button
          onClick={() => setActiveTab('expectations')}
          className={clsx(
            'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px',
            activeTab === 'expectations'
              ? 'border-amber-500 text-amber-700 bg-white'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          <Users className="h-4 w-4" />
          Patient Expectations
        </button>
        <button
          onClick={() => setActiveTab('criteria')}
          className={clsx(
            'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px',
            activeTab === 'criteria'
              ? 'border-amber-500 text-amber-700 bg-white'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          <Search className="h-4 w-4" />
          Matching Criteria
        </button>
        {documentFindings?.hasDocuments && (
          <button
            onClick={() => setActiveTab('documents')}
            className={clsx(
              'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px',
              activeTab === 'documents'
                ? 'border-amber-500 text-amber-700 bg-white'
                : 'border-transparent text-gray-500 hover:text-gray-700',
              documentFindings.hasInconsistencies && 'text-red-600'
            )}
          >
            <FileText className="h-4 w-4" />
            Documents
            {documentFindings.hasInconsistencies && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">!</span>
            )}
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div className="p-4 bg-white">
        {/* Summary Tab */}
        {activeTab === 'summary' && indication && (
          <div className="space-y-4">
            {/* Key Points - Andy Pro style */}
            <div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Key Points</h4>
              <div className="flex flex-wrap gap-2">
                <span className={clsx(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium',
                  indication.suitability === 'SUITABLE'
                    ? 'bg-green-100 text-green-700'
                    : indication.suitability === 'UNSUITABLE'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-amber-100 text-amber-700'
                )}>
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold">1</span>
                  {indication.suitability.replace('_', ' ')}
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-700">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold">2</span>
                  {indication.confidence}% Confidence
                </span>
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold">3</span>
                  {indication.issues.length} Issues
                </span>
                {criteria?.criteria && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-purple-100 text-purple-700">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white text-xs font-bold">4</span>
                    {criteria.criteria.length} Criteria
                  </span>
                )}
              </div>
            </div>

            {/* Summary */}
            <div>
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Case Summary</h4>
              <p className="text-sm text-gray-700 leading-relaxed">{indication.summary}</p>
            </div>

            {/* Issues Preview */}
            {indication.issues.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Issues Overview</h4>
                <div className="space-y-2">
                  {indication.issues.slice(0, 3).map((issue, idx) => (
                    <div key={idx} className="flex items-start gap-2">
                      <span className={clsx(
                        'px-2 py-0.5 rounded text-xs font-medium capitalize',
                        SEVERITY_COLORS[issue.severity] || 'bg-gray-100 text-gray-600'
                      )}>
                        {issue.severity}
                      </span>
                      <span className="text-sm text-gray-600">{issue.description}</span>
                    </div>
                  ))}
                  {indication.issues.length > 3 && (
                    <p className="text-xs text-gray-500">
                      + {indication.issues.length - 3} more issues (see Medical Assessment tab)
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Medical Assessment Tab */}
        {activeTab === 'assessment' && indication && (
          <div className="space-y-4">
            {/* Suitability Decision */}
            <div className="p-4 rounded-lg bg-gray-50">
              <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Stated Indication</h4>
              <div className="flex items-center gap-3">
                <span className={clsx(
                  'px-3 py-1.5 rounded-lg text-sm font-semibold',
                  indication.suitability === 'SUITABLE'
                    ? 'bg-green-100 text-green-700'
                    : indication.suitability === 'UNSUITABLE'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-amber-100 text-amber-700'
                )}>
                  {indication.suitability.replace('_', ' ')}
                </span>
                <span className="text-sm text-gray-500">
                  with {indication.confidence}% confidence
                </span>
              </div>
            </div>

            {/* All Issues */}
            {indication.issues.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  All Issues ({indication.issues.length})
                </h4>
                <div className="space-y-3">
                  {indication.issues.map((issue, idx) => (
                    <div key={idx} className="p-3 rounded-lg border border-gray-200 bg-white">
                      <div className="flex items-start gap-3">
                        <span className={clsx(
                          'px-2 py-0.5 rounded text-xs font-medium capitalize shrink-0',
                          SEVERITY_COLORS[issue.severity] || 'bg-gray-100 text-gray-600'
                        )}>
                          {issue.severity}
                        </span>
                        <div className="flex-1">
                          <p className="text-sm text-gray-700">{issue.description}</p>
                          {issue.recommendation && (
                            <p className="mt-2 text-xs text-gray-500 flex items-start gap-1">
                              <Lightbulb className="h-3 w-3 mt-0.5 shrink-0" />
                              {issue.recommendation}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {indication.issues.length === 0 && (
              <div className="p-4 rounded-lg bg-green-50 text-center">
                <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm text-green-700">No issues identified</p>
              </div>
            )}
          </div>
        )}

        {/* Patient Expectations Tab */}
        {activeTab === 'expectations' && indication && (
          <div className="space-y-4">
            {indication.patientExpectations ? (
              <>
                {/* Expectations Summary */}
                <div className="p-4 rounded-lg bg-gray-50">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                    Expectation Assessment
                  </h4>
                  <p className="text-sm text-gray-700">{indication.patientExpectations.summary}</p>
                </div>

                {/* Communication Needs / Concerns */}
                {indication.patientExpectations.concerns && indication.patientExpectations.concerns.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" />
                      Communication Needs
                    </h4>
                    <ul className="space-y-2">
                      {indication.patientExpectations.concerns.map((concern, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                          <span className="text-amber-500 mt-1">•</span>
                          {concern}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Goals / Ideal Provider Traits */}
                {indication.patientExpectations.goals && indication.patientExpectations.goals.length > 0 && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Ideal Provider Traits
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {indication.patientExpectations.goals.map((goal, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm bg-blue-50 text-blue-700"
                        >
                          {goal}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="p-4 rounded-lg bg-gray-50 text-center">
                <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No patient expectations data available</p>
              </div>
            )}
          </div>
        )}

        {/* Matching Criteria Tab */}
        {activeTab === 'criteria' && (
          <div className="space-y-4">
            {criteria?.criteria && criteria.criteria.length > 0 ? (
              <>
                {/* Priority Explanation */}
                {criteria.priorityExplanation && (
                  <div className="p-4 rounded-lg bg-gray-50">
                    <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Priority Rationale
                    </h4>
                    <p className="text-sm text-gray-700">{criteria.priorityExplanation}</p>
                  </div>
                )}

                {/* Matching Criteria by Priority */}
                {['critical', 'high', 'medium', 'low'].map(priority => {
                  const priorityCriteria = criteria.criteria.filter(
                    c => normalizePriority(c.priority) === priority
                  );
                  if (priorityCriteria.length === 0) return null;

                  return (
                    <div key={priority}>
                      <h4 className={clsx(
                        'text-xs font-semibold uppercase tracking-wide mb-2 flex items-center gap-2',
                        priority === 'critical' ? 'text-red-700' :
                        priority === 'high' ? 'text-orange-700' :
                        priority === 'medium' ? 'text-amber-700' : 'text-gray-600'
                      )}>
                        <span className={clsx(
                          'w-2 h-2 rounded-full',
                          priority === 'critical' ? 'bg-red-500' :
                          priority === 'high' ? 'bg-orange-500' :
                          priority === 'medium' ? 'bg-amber-500' : 'bg-gray-400'
                        )} />
                        {priority.charAt(0).toUpperCase() + priority.slice(1)} Priority ({priorityCriteria.length})
                      </h4>
                      <div className="space-y-2">
                        {priorityCriteria.map((criterion, idx) => (
                          <div
                            key={idx}
                            className={clsx(
                              'p-3 rounded-lg border',
                              PRIORITY_COLORS[priority] || 'border-gray-200 bg-white'
                            )}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">
                                  {criterion.name || criterion.field}
                                </p>
                                <p className="mt-1 text-sm text-gray-600">
                                  {Array.isArray(criterion.value)
                                    ? criterion.value.join(', ')
                                    : criterion.value}
                                </p>
                                {criterion.reasoning && (
                                  <p className="mt-2 text-xs text-gray-500">
                                    {criterion.reasoning}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <div className="p-4 rounded-lg bg-gray-50 text-center">
                <Search className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No matching criteria generated</p>
              </div>
            )}
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && documentFindings && (
          <div className="space-y-4">
            {/* Verification Status */}
            {documentFindings.verificationStatus && (
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                <h4 className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
                  Verification Status
                </h4>
                <p className="text-sm text-blue-700">{documentFindings.verificationStatus}</p>
              </div>
            )}

            {/* Inconsistencies Warning */}
            {documentFindings.hasInconsistencies && documentFindings.inconsistencies && documentFindings.inconsistencies.length > 0 && (
              <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                <h4 className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Inconsistencies Detected ({documentFindings.inconsistencies.length})
                </h4>
                <div className="space-y-3">
                  {documentFindings.inconsistencies.map((inconsistency, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-white border border-red-200">
                      <div className="flex items-start gap-3">
                        <span className={clsx(
                          'px-2 py-0.5 rounded text-xs font-medium capitalize shrink-0',
                          SEVERITY_COLORS[inconsistency.severity] || 'bg-gray-100 text-gray-600'
                        )}>
                          {inconsistency.severity}
                        </span>
                        <div className="flex-1">
                          <p className="text-sm text-gray-700">{inconsistency.description}</p>
                          <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                            <span>
                              <strong>Document:</strong> {inconsistency.documentSource}
                            </span>
                            {inconsistency.questionnaireSource && (
                              <span>
                                <strong>Question:</strong> {inconsistency.questionnaireSource}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Document Findings */}
            {documentFindings.findings && documentFindings.findings.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">
                  Document Analysis ({documentFindings.findings.length})
                </h4>
                <div className="space-y-3">
                  {documentFindings.findings.map((finding, idx) => (
                    <div key={idx} className="p-4 rounded-lg border border-gray-200 bg-white">
                      <div className="flex items-start gap-3">
                        <FileText className="h-5 w-5 text-gray-400 shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <h5 className="font-medium text-gray-900 mb-2">{finding.documentName}</h5>

                          {finding.observations && finding.observations.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs font-medium text-gray-500 mb-1">Observations:</p>
                              <ul className="list-disc list-inside space-y-1">
                                {finding.observations.map((obs, obsIdx) => (
                                  <li key={obsIdx} className="text-sm text-gray-700">{obs}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {finding.clinicalSignificance && (
                            <div className="p-2 rounded bg-amber-50 border border-amber-200">
                              <p className="text-xs font-medium text-amber-700 mb-1">Clinical Significance:</p>
                              <p className="text-sm text-amber-900">{finding.clinicalSignificance}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No Findings */}
            {(!documentFindings.findings || documentFindings.findings.length === 0) &&
             (!documentFindings.inconsistencies || documentFindings.inconsistencies.length === 0) && (
              <div className="p-4 rounded-lg bg-gray-50 text-center">
                <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Documents were attached but no analysis findings are available</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

type ViewMode = 'list' | 'comparison';

export function TestRunResults() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expandedExecutions, setExpandedExecutions] = useState<Set<string>>(new Set());
  const [expandedSearchExecutions, setExpandedSearchExecutions] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [highlightWinner, setHighlightWinner] = useState(true);

  // Check if navigated from evaluations list
  const fromEvaluations = searchParams.get('from') === 'evaluations';

  const { data: testRun, isLoading, error } = useQuery({
    queryKey: ['validation', 'test-run', id, 'results'],
    queryFn: async () => {
      const response = await api.get<{ data: TestRun }>(`/api/validation/test-runs/${id}`);
      return response.data;
    },
    enabled: !!id,
  });

  // Fetch quality assessment results (Phase 5)
  const { data: qualityAssessmentData } = useQuery({
    queryKey: ['validation', 'quality-assessment-results', id],
    queryFn: async () => {
      const response = await api.qualityAssessment.getResults(id!);
      return response.data;
    },
    enabled: !!id && !!testRun?.isRevealed,
  });

  // Fetch AI quality assessment (automated scoring)
  const { data: aiAssessmentData, isLoading: isLoadingAIAssessment, refetch: refetchAIAssessment } = useQuery({
    queryKey: ['validation', 'ai-quality-assessment', id],
    queryFn: async () => {
      try {
        const response = await api.qualityAssessment.getAIAssessment(id!);
        return response.data;
      } catch {
        // AI assessment may not exist yet
        return null;
      }
    },
    enabled: !!id && !!testRun?.isRevealed,
  });

  // Mutation to trigger AI quality assessment
  // Always pass force=true to allow re-running evaluations
  const triggerAIAssessmentMutation = useMutation({
    mutationFn: async () => {
      const response = await api.qualityAssessment.triggerAIAssessment(id!, true);
      return response.data;
    },
    onSuccess: () => {
      // Refetch AI assessment data after successful trigger
      refetchAIAssessment();
    },
  });

  const toggleExpanded = (executionId: string) => {
    setExpandedExecutions(prev => {
      const next = new Set(prev);
      if (next.has(executionId)) {
        next.delete(executionId);
      } else {
        next.add(executionId);
      }
      return next;
    });
  };

  const toggleSearchExpanded = (searchExecutionId: string) => {
    setExpandedSearchExecutions(prev => {
      const next = new Set(prev);
      if (next.has(searchExecutionId)) {
        next.delete(searchExecutionId);
      } else {
        next.add(searchExecutionId);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <LoadingState
        message="Loading test run results..."
        showLongLoadingMessage={true}
        longLoadingThreshold={5000}
        longLoadingMessage="Loading detailed results and model analysis data."
      />
    );
  }

  if (error || !testRun) {
    return (
      <ErrorState
        title="Test Run Not Found"
        message={error instanceof Error ? error.message : "The test run you're looking for doesn't exist or has been deleted."}
        helpText="Check the URL or go back to the test runs list"
        onRetry={() => queryClient.invalidateQueries({ queryKey: ['validation', 'test-run', id] })}
        variant="card"
      />
    );
  }

  if (!testRun.isRevealed) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-8 text-center">
        <Eye className="mx-auto h-10 w-10 text-amber-500" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900">Results Not Yet Revealed</h2>
        <p className="mt-1 text-sm text-gray-500">
          Complete the blind evaluation first to reveal results.
        </p>
        <Link
          to={`/validation/test-runs/${id}/evaluate`}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
        >
          <Eye className="h-4 w-4" />
          Start Evaluation
        </Link>
      </div>
    );
  }

  const completedExecutions = testRun.executions.filter((e) => e.status === 'COMPLETED');
  const latestEvaluation = testRun.evaluationSets[testRun.evaluationSets.length - 1];

  // Calculate rankings based on evaluations
  const getRankForExecution = (blindCode: string): number => {
    if (!latestEvaluation?.rankings) return 999;
    const index = latestEvaluation.rankings.indexOf(blindCode);
    return index >= 0 ? index + 1 : 999;
  };

  // Sort executions by rank
  const sortedExecutions = [...completedExecutions].sort(
    (a, b) => getRankForExecution(a.blindCode) - getRankForExecution(b.blindCode)
  );

  // Calculate totals
  const totalCost = completedExecutions.reduce(
    (sum, e) => sum + (e.indicationCostUsd || 0) + (e.criteriaCostUsd || 0),
    0
  );
  const totalTime = completedExecutions.reduce(
    (sum, e) => sum + (e.indicationLatencyMs || 0) + (e.criteriaLatencyMs || 0),
    0
  );

  // Determine Phase 1 winner
  const winnerBlindCode = latestEvaluation?.rankings?.[0];
  const winnerExecution = completedExecutions.find((e) => e.blindCode === winnerBlindCode);

  // Determine Phase 2 (search) winner
  const allSearchExecutions = testRun.executions.flatMap((exec) =>
    (exec.searchExecutions || []).map((se) => ({
      ...se,
      criteriaModel: exec.model,
      criteriaBlindCode: exec.blindCode,
    }))
  );
  // Find a completed evaluation set with rankings (not just the last one)
  const completedSearchEvaluation = testRun.searchEvaluationSets?.find(
    (evalSet) => evalSet.completedAt && evalSet.rankings && evalSet.rankings.length > 0
  ) || testRun.searchEvaluationSets?.[testRun.searchEvaluationSets.length - 1];
  const searchWinnerBlindCode = completedSearchEvaluation?.rankings?.[0];
  const searchWinnerExecution = allSearchExecutions.find((se) => se.blindCode === searchWinnerBlindCode);
  const hasPhase2 = testRun.searchPhaseStatus === 'EVALUATED' && searchWinnerExecution;

  // Breadcrumb items - adjust based on navigation source
  const breadcrumbItems = fromEvaluations
    ? [
        { label: 'Model Validation', href: '/validation', icon: Home },
        { label: 'Evaluations', href: '/validation/evaluations', icon: ClipboardCheck },
        { label: testRun.name },
        { label: 'Results', icon: BarChart3 },
      ]
    : [
        { label: 'Model Validation', href: '/validation', icon: Home },
        { label: 'Test Runs', href: '/validation/test-runs', icon: Play },
        { label: testRun.name, href: `/validation/test-runs/${id}` },
        { label: 'Results', icon: BarChart3 },
      ];

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb items={breadcrumbItems} />

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate(fromEvaluations ? '/validation/evaluations' : `/validation/test-runs/${id}`)}
            className="rounded-lg p-2 hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">Results: {testRun.name}</h1>
              <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
                <Eye className="h-3 w-3" />
                Revealed
              </span>
            </div>
            <p className="text-sm text-gray-500">
              Test Case: {testRun.testCase.name} ({testRun.testCase.code})
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1">
            <button
              onClick={() => setViewMode('list')}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                viewMode === 'list'
                  ? 'bg-amber-100 text-amber-700'
                  : 'text-gray-600 hover:bg-gray-50'
              )}
            >
              <List className="h-4 w-4" />
              List
            </button>
            <button
              onClick={() => setViewMode('comparison')}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                viewMode === 'comparison'
                  ? 'bg-amber-100 text-amber-700'
                  : 'text-gray-600 hover:bg-gray-50'
              )}
            >
              <Columns className="h-4 w-4" />
              Compare
            </button>
          </div>

          {/* Highlight Winner Toggle */}
          <label className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 cursor-pointer">
            <input
              type="checkbox"
              checked={highlightWinner}
              onChange={(e) => setHighlightWinner(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
            />
            <span className="text-sm text-gray-700">Highlight Winner</span>
          </label>
        </div>
      </div>

      {/* Winner Banners */}
      {highlightWinner && (winnerExecution || searchWinnerExecution || qualityAssessmentData?.summary?.topProvider || (aiAssessmentData && aiAssessmentData.status === 'COMPLETED')) && (
        <div className={clsx(
          'grid gap-4',
          // Dynamic grid: 1, 2, 3, or 4 columns depending on winners
          (() => {
            const count = [winnerExecution, searchWinnerExecution, qualityAssessmentData?.summary?.topProvider, aiAssessmentData?.status === 'COMPLETED'].filter(Boolean).length;
            if (count >= 4) return 'sm:grid-cols-2 lg:grid-cols-4';
            if (count === 3) return 'sm:grid-cols-3';
            if (count === 2) return 'sm:grid-cols-2';
            return 'sm:grid-cols-1';
          })()
        )}>
          {/* Phase 2+3: Case Analysis Winner */}
          {winnerExecution && (
            <div className="rounded-xl border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-200">
                  <Crown className="h-5 w-5 text-amber-700" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-amber-600 uppercase tracking-wide">Phase 2+3: Case Analysis Winner</span>
                    <Trophy className="h-3.5 w-3.5 text-amber-500" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <ModelBadge model={winnerExecution.model} />
                <span className="text-xs text-gray-500 font-mono">
                  {winnerExecution.blindCode}
                </span>
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                {winnerExecution.autoScore && (
                  <span>{Math.round(winnerExecution.autoScore.combinedScore)}% score</span>
                )}
                <span>{((winnerExecution.indicationLatencyMs || 0) + (winnerExecution.criteriaLatencyMs || 0)) / 1000}s</span>
                <span>${((winnerExecution.indicationCostUsd || 0) + (winnerExecution.criteriaCostUsd || 0)).toFixed(4)}</span>
              </div>
              {/* Show Grand Rounds config if applicable */}
              {winnerExecution.model === 'GRAND_ROUNDS' && testRun.config?.grandRoundsConfig && (
                <GrandRoundsConfigDisplay config={testRun.config.grandRoundsConfig} />
              )}
            </div>
          )}

          {/* Phase 4: Care Provider Matching Winner */}
          {hasPhase2 && searchWinnerExecution && (
            <div className="rounded-xl border-2 border-blue-300 bg-gradient-to-r from-blue-50 to-cyan-50 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-200">
                  <Crown className="h-5 w-5 text-blue-700" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-blue-600 uppercase tracking-wide">Phase 4: Care Provider Matching Winner</span>
                    <Trophy className="h-3.5 w-3.5 text-blue-500" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <ModelBadge model={searchWinnerExecution.searchModel} />
                <span className="text-xs text-gray-400">with criteria from</span>
                <ModelBadge model={searchWinnerExecution.criteriaModel} />
              </div>
              <div className="mt-1">
                <span className="text-xs text-gray-500 font-mono">
                  {searchWinnerExecution.blindCode}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                {searchWinnerExecution.autoScore && (
                  <span>{Math.round(searchWinnerExecution.autoScore.combinedScore || 0)}% score</span>
                )}
                <span>{((searchWinnerExecution.latencyMs || 0) / 1000).toFixed(1)}s</span>
                <span>${(searchWinnerExecution.costUsd || 0).toFixed(4)}</span>
              </div>
            </div>
          )}

          {/* Phase 5: Quality Assessment Winner (Human) */}
          {qualityAssessmentData?.summary?.winningModel && (
            <div className="rounded-xl border-2 border-green-300 bg-gradient-to-r from-green-50 to-emerald-50 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-200">
                  <Crown className="h-5 w-5 text-green-700" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-green-600 uppercase tracking-wide">Phase 5: Human Assessment</span>
                    <Users className="h-3.5 w-3.5 text-green-500" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <ModelBadge model={qualityAssessmentData.summary.winningModel.model} />
              </div>
              <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                <span>{qualityAssessmentData.summary.winningModel.avgScore}/16 avg score</span>
                <span>{Math.round(qualityAssessmentData.summary.winningModel.goodMatchRate * 100)}% good match</span>
                <span>{qualityAssessmentData.summary.winningModel.totalProviders} provider{qualityAssessmentData.summary.winningModel.totalProviders !== 1 ? 's' : ''}</span>
              </div>
            </div>
          )}

          {/* Phase 6: AI Quality Assessment Winner */}
          {aiAssessmentData && aiAssessmentData.status === 'COMPLETED' && aiAssessmentData.modelRankings?.[0] && (
            <div className="rounded-xl border-2 border-indigo-300 bg-gradient-to-r from-indigo-50 to-purple-50 p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-200">
                  <Crown className="h-5 w-5 text-indigo-700" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-indigo-600 uppercase tracking-wide">Phase 6: AI Assessment</span>
                    <Bot className="h-3.5 w-3.5 text-indigo-500" />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <ModelBadge model={aiAssessmentData.modelRankings[0].model} />
              </div>
              <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                <span>{aiAssessmentData.modelRankings[0].avgScore.toFixed(1)}/16 avg score</span>
                <span>{aiAssessmentData.modelRankings[0].goodMatchRate}% good match</span>
                <span>{aiAssessmentData.modelRankings[0].totalProviders} provider{aiAssessmentData.modelRankings[0].totalProviders !== 1 ? 's' : ''}</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-amber-100 p-2">
              <BarChart3 className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{completedExecutions.length}</p>
              <p className="text-sm text-gray-500">Models Tested</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-blue-100 p-2">
              <FileText className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{testRun.evaluationSets.length}</p>
              <p className="text-sm text-gray-500">Evaluations</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-green-100 p-2">
              <Clock className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{(totalTime / 1000).toFixed(1)}s</p>
              <p className="text-sm text-gray-500">Total Time</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-purple-100 p-2">
              <DollarSign className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">${totalCost.toFixed(3)}</p>
              <p className="text-sm text-gray-500">Total Cost</p>
            </div>
          </div>
        </div>
      </div>

      {/* Leaderboard - List View */}
      {viewMode === 'list' && (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="border-b border-gray-100 bg-gray-50 px-6 py-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Trophy className="h-5 w-5 text-amber-500" />
              Phase 2+3: Case Analysis Results
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Click on an entry to expand full analysis details
            </p>
            <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
              <span>{completedExecutions.length} model{completedExecutions.length !== 1 ? 's' : ''}</span>
              <span>•</span>
              <span>{totalCost.toFixed(3)} USD total</span>
              <span>•</span>
              <span>{(totalTime / 1000).toFixed(1)}s total</span>
            </div>
          </div>
          <div className="divide-y divide-gray-100">
            {sortedExecutions.map((execution, index) => {
              const rank = getRankForExecution(execution.blindCode);
              const verdict = latestEvaluation?.items?.find(item => item.blindCode === execution.blindCode)?.verdict;
              const isExpanded = expandedExecutions.has(execution.id);
              const isWinner = highlightWinner && index === 0;

              return (
                <div
                  key={execution.id}
                  className={clsx(
                    isWinner && 'bg-gradient-to-r from-amber-50 to-yellow-50 border-l-4 border-l-amber-400'
                  )}
                >
                {/* Clickable Header */}
                <button
                  onClick={() => toggleExpanded(execution.id)}
                  className="w-full p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-6">
                    <div className="flex items-start gap-4">
                      <div
                        className={clsx(
                          'flex h-10 w-10 items-center justify-center rounded-full font-bold',
                          index === 0
                            ? 'bg-amber-200 text-amber-800'
                            : index === 1
                            ? 'bg-gray-200 text-gray-700'
                            : index === 2
                            ? 'bg-orange-200 text-orange-800'
                            : 'bg-gray-100 text-gray-500'
                        )}
                      >
                        {rank}
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <ModelBadge model={execution.model} />
                          <span className="text-xs text-gray-400 font-mono">
                            {execution.blindCode}
                          </span>
                          {isWinner && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
                              <Crown className="h-3 w-3" />
                              Winner
                            </span>
                          )}
                        </div>
                        {execution.modelVersion && (
                          <p className="mt-1 text-xs text-gray-500">
                            Version: {execution.modelVersion}
                          </p>
                        )}
                        {/* Show Grand Rounds config for GRAND_ROUNDS model */}
                        {execution.model === 'GRAND_ROUNDS' && testRun.config?.grandRoundsConfig && (
                          <div className="mt-2 inline-flex items-center gap-2 text-xs">
                            <span className={clsx(
                              'px-2 py-0.5 rounded-full font-medium border',
                              testRun.config.grandRoundsConfig.promptVersion === 'v2'
                                ? 'bg-indigo-100 text-indigo-700 border-indigo-200'
                                : 'bg-gray-100 text-gray-600 border-gray-200'
                            )}>
                              {testRun.config.grandRoundsConfig.promptVersion === 'v2' ? 'Joint Evidence' : 'Democracy'}
                            </span>
                          </div>
                        )}
                        <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                          <span>
                            <Clock className="inline h-3 w-3 mr-1" />
                            {((execution.indicationLatencyMs || 0) + (execution.criteriaLatencyMs || 0)) / 1000}s
                          </span>
                          <span>
                            <DollarSign className="inline h-3 w-3 mr-1" />
                            {((execution.indicationCostUsd || 0) + (execution.criteriaCostUsd || 0)).toFixed(4)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      {/* Quick Stats */}
                      {execution.indication && (
                        <div className="hidden sm:flex items-center gap-3 text-xs">
                          <span className={clsx(
                            'px-2 py-1 rounded',
                            execution.indication.suitability === 'SUITABLE'
                              ? 'bg-green-100 text-green-700'
                              : execution.indication.suitability === 'UNSUITABLE'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-amber-100 text-amber-700'
                          )}>
                            {execution.indication.suitability.replace('_', ' ')}
                          </span>
                          <span className="text-gray-500">
                            {execution.indication.confidence}% conf
                          </span>
                        </div>
                      )}

                      {/* Auto Score - always shown for alignment */}
                      <div className="w-28 hidden md:block">
                        <ScoreBar
                          score={execution.autoScore?.combinedScore || 0}
                          label="Auto Score"
                        />
                      </div>

                      {/* Human Verdict - always shown for alignment */}
                      <div className="w-24 text-right hidden sm:block">
                        <span className="text-xs text-gray-500">Verdict</span>
                        <div className="mt-1">
                          {verdict ? (
                            <VerdictDisplay verdict={verdict} />
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </div>
                      </div>

                      {/* Expand Icon */}
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100">
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        )}
                      </div>
                    </div>
                  </div>
                </button>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-6 pb-6">
                    <ExecutionDetails execution={execution} />
                  </div>
                )}
              </div>
            );
            })}
          </div>
        </div>
      )}

      {/* Comparison View */}
      {viewMode === 'comparison' && (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="border-b border-gray-100 bg-gray-50 px-6 py-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Columns className="h-5 w-5 text-blue-500" />
              Side-by-Side Comparison
            </h2>
            <p className="text-xs text-gray-500 mt-1">Compare model outputs across key metrics</p>
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Metric
                  </th>
                  {sortedExecutions.map((execution, index) => {
                    const isWinner = highlightWinner && index === 0;
                    return (
                      <th
                        key={execution.id}
                        className={clsx(
                          'px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider min-w-[180px]',
                          isWinner ? 'bg-amber-50 text-amber-700' : 'text-gray-500'
                        )}
                      >
                        <div className="flex flex-col items-center gap-1">
                          <ModelBadge model={execution.model} />
                          {isWinner && (
                            <div className="flex items-center gap-1 text-amber-600">
                              <Crown className="h-3 w-3" />
                              <span className="text-[10px]">Winner</span>
                            </div>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {/* Rank Row */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Rank</td>
                  {sortedExecutions.map((execution, index) => {
                    const rank = getRankForExecution(execution.blindCode);
                    const isWinner = highlightWinner && index === 0;
                    return (
                      <td
                        key={execution.id}
                        className={clsx(
                          'px-6 py-4 text-center',
                          isWinner && 'bg-amber-50'
                        )}
                      >
                        <div
                          className={clsx(
                            'inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold mx-auto',
                            index === 0
                              ? 'bg-amber-200 text-amber-800'
                              : index === 1
                              ? 'bg-gray-200 text-gray-700'
                              : index === 2
                              ? 'bg-orange-200 text-orange-800'
                              : 'bg-gray-100 text-gray-500'
                          )}
                        >
                          {rank}
                        </div>
                      </td>
                    );
                  })}
                </tr>

                {/* Suitability Row */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Suitability</td>
                  {sortedExecutions.map((execution, index) => {
                    const isWinner = highlightWinner && index === 0;
                    return (
                      <td
                        key={execution.id}
                        className={clsx(
                          'px-6 py-4 text-center',
                          isWinner && 'bg-amber-50'
                        )}
                      >
                        {execution.indication ? (
                          <span className={clsx(
                            'inline-flex px-2 py-1 rounded text-xs font-medium',
                            execution.indication.suitability === 'SUITABLE'
                              ? 'bg-green-100 text-green-700'
                              : execution.indication.suitability === 'UNSUITABLE'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-amber-100 text-amber-700'
                          )}>
                            {execution.indication.suitability.replace('_', ' ')}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    );
                  })}
                </tr>

                {/* Confidence Row */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Confidence</td>
                  {sortedExecutions.map((execution, index) => {
                    const isWinner = highlightWinner && index === 0;
                    const confidence = execution.indication?.confidence || 0;
                    return (
                      <td
                        key={execution.id}
                        className={clsx(
                          'px-6 py-4 text-center',
                          isWinner && 'bg-amber-50'
                        )}
                      >
                        <div className="flex flex-col items-center gap-1">
                          <span className={clsx(
                            'text-lg font-bold',
                            confidence >= 80 ? 'text-green-600' : confidence >= 60 ? 'text-amber-600' : 'text-red-600'
                          )}>
                            {confidence}%
                          </span>
                          <div className="w-16 h-1.5 bg-gray-200 rounded-full">
                            <div
                              className={clsx(
                                'h-full rounded-full',
                                confidence >= 80 ? 'bg-green-500' : confidence >= 60 ? 'bg-amber-500' : 'bg-red-500'
                              )}
                              style={{ width: `${confidence}%` }}
                            />
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>

                {/* Auto Score Row */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Auto Score</td>
                  {sortedExecutions.map((execution, index) => {
                    const isWinner = highlightWinner && index === 0;
                    const score = execution.autoScore?.combinedScore || 0;
                    return (
                      <td
                        key={execution.id}
                        className={clsx(
                          'px-6 py-4 text-center',
                          isWinner && 'bg-amber-50'
                        )}
                      >
                        {execution.autoScore ? (
                          <div className="flex flex-col items-center gap-1">
                            <span className={clsx(
                              'text-lg font-bold',
                              score >= 80 ? 'text-green-600' : score >= 60 ? 'text-amber-600' : 'text-red-600'
                            )}>
                              {Math.round(score)}%
                            </span>
                            <div className="w-16 h-1.5 bg-gray-200 rounded-full">
                              <div
                                className={clsx(
                                  'h-full rounded-full',
                                  score >= 80 ? 'bg-green-500' : score >= 60 ? 'bg-amber-500' : 'bg-red-500'
                                )}
                                style={{ width: `${score}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    );
                  })}
                </tr>

                {/* Latency Row */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Latency</td>
                  {sortedExecutions.map((execution, index) => {
                    const isWinner = highlightWinner && index === 0;
                    const latency = ((execution.indicationLatencyMs || 0) + (execution.criteriaLatencyMs || 0)) / 1000;
                    const validLatencies = sortedExecutions
                      .map(e => ((e.indicationLatencyMs || 0) + (e.criteriaLatencyMs || 0)) / 1000)
                      .filter(l => l > 0);
                    const minLatency = validLatencies.length > 0 ? Math.min(...validLatencies) : 0;
                    const isFastest = latency === minLatency && latency > 0;
                    return (
                      <td
                        key={execution.id}
                        className={clsx(
                          'px-6 py-4 text-center',
                          isWinner && 'bg-amber-50'
                        )}
                      >
                        <div className="flex flex-col items-center gap-0.5">
                          <span className={clsx(
                            'text-lg font-bold',
                            isFastest ? 'text-green-600' : 'text-gray-700'
                          )}>
                            {latency.toFixed(1)}s
                          </span>
                          {isFastest && (
                            <span className="text-[10px] text-green-600 font-medium">Fastest</span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>

                {/* Cost Row */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Cost</td>
                  {sortedExecutions.map((execution, index) => {
                    const isWinner = highlightWinner && index === 0;
                    const cost = (execution.indicationCostUsd || 0) + (execution.criteriaCostUsd || 0);
                    const validCosts = sortedExecutions
                      .map(e => (e.indicationCostUsd || 0) + (e.criteriaCostUsd || 0))
                      .filter(c => c > 0);
                    const minCost = validCosts.length > 0 ? Math.min(...validCosts) : 0;
                    const isCheapest = cost === minCost && cost > 0;
                    return (
                      <td
                        key={execution.id}
                        className={clsx(
                          'px-6 py-4 text-center',
                          isWinner && 'bg-amber-50'
                        )}
                      >
                        <div className="flex flex-col items-center gap-0.5">
                          <span className={clsx(
                            'text-lg font-bold',
                            isCheapest ? 'text-green-600' : 'text-gray-700'
                          )}>
                            ${cost.toFixed(4)}
                          </span>
                          {isCheapest && (
                            <span className="text-[10px] text-green-600 font-medium">Cheapest</span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>

                {/* Human Verdict Row */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Human Verdict</td>
                  {sortedExecutions.map((execution, index) => {
                    const isWinner = highlightWinner && index === 0;
                    const verdict = latestEvaluation?.items?.find(item => item.blindCode === execution.blindCode)?.verdict;
                    return (
                      <td
                        key={execution.id}
                        className={clsx(
                          'px-6 py-4 text-center',
                          isWinner && 'bg-amber-50'
                        )}
                      >
                        {verdict ? (
                          <VerdictDisplay verdict={verdict} />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                    );
                  })}
                </tr>

                {/* Issues Count Row */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Issues Found</td>
                  {sortedExecutions.map((execution, index) => {
                    const isWinner = highlightWinner && index === 0;
                    const issueCount = execution.indication?.issues?.length || 0;
                    return (
                      <td
                        key={execution.id}
                        className={clsx(
                          'px-6 py-4 text-center',
                          isWinner && 'bg-amber-50'
                        )}
                      >
                        <span className={clsx(
                          'inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium',
                          issueCount === 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        )}>
                          {issueCount}
                        </span>
                      </td>
                    );
                  })}
                </tr>

                {/* Criteria Count Row */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Matching Criteria</td>
                  {sortedExecutions.map((execution, index) => {
                    const isWinner = highlightWinner && index === 0;
                    const criteriaCount = execution.criteria?.criteria?.length || 0;
                    return (
                      <td
                        key={execution.id}
                        className={clsx(
                          'px-6 py-4 text-center',
                          isWinner && 'bg-amber-50'
                        )}
                      >
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-purple-100 text-purple-700 text-sm font-medium">
                          {criteriaCount}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Phase 2+3: Analysis Evaluation History */}
      {testRun.evaluationSets.length > 0 && (() => {
        // Create mapping from blind code to model for Phase 2+3
        const blindCodeToModel = new Map(
          testRun.executions.map((exec) => [exec.blindCode, exec.model])
        );

        return (
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Analysis Evaluation History</h2>
            <div className="space-y-3">
              {testRun.evaluationSets.map((evaluation) => (
                <div
                  key={evaluation.id}
                  className="flex items-start justify-between rounded-lg bg-gray-50 p-4"
                >
                  <div>
                    <p className="font-medium text-gray-900">{evaluation.evaluator.name}</p>
                    <p className="text-xs text-gray-500">
                      {format(new Date(evaluation.createdAt), 'PPp')}
                    </p>
                    {evaluation.evaluatorNotes && (
                      <p className="mt-2 text-sm text-gray-600">{evaluation.evaluatorNotes}</p>
                    )}
                  </div>
                  <div className="text-right text-sm">
                    <p className="text-gray-500 mb-2">Ranking</p>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      {evaluation.rankings.map((blindCode, idx) => {
                        const model = blindCodeToModel.get(blindCode);
                        return (
                          <div key={blindCode} className="flex items-center gap-1">
                            {idx > 0 && <span className="text-gray-400">→</span>}
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-gray-500">#{idx + 1}</span>
                              {model ? <ModelBadge model={model} /> : <span className="font-mono text-gray-700">{blindCode}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Phase 4: Care Provider Matching Results */}
      {testRun.searchPhaseStatus === 'EVALUATED' && (() => {
        // Collect all search executions with their evaluations
        const allSearchExecutions = testRun.executions.flatMap((exec) =>
          (exec.searchExecutions || []).map((se) => ({
            ...se,
            criteriaModel: exec.model,
            criteriaBlindCode: exec.blindCode,
          }))
        );

        // Find a completed evaluation set with rankings (not just the last one)
        const completedSearchEval = testRun.searchEvaluationSets?.find(
          (evalSet) => evalSet.completedAt && evalSet.rankings && evalSet.rankings.length > 0
        ) || testRun.searchEvaluationSets?.[testRun.searchEvaluationSets.length - 1];

        const getSearchRank = (blindCode: string): number => {
          if (!completedSearchEval?.rankings) return 999;
          const index = completedSearchEval.rankings.indexOf(blindCode);
          return index >= 0 ? index + 1 : 999;
        };

        const sortedSearchExecutions = [...allSearchExecutions].sort(
          (a, b) => getSearchRank(a.blindCode) - getSearchRank(b.blindCode)
        );

        const totalSearchCost = allSearchExecutions.reduce((sum, se) => sum + (se.costUsd || 0), 0);
        const totalSearchTime = allSearchExecutions.reduce((sum, se) => sum + (se.latencyMs || 0), 0);

        // Determine search winner
        const searchWinnerBlindCode = completedSearchEval?.rankings?.[0];

        return (
          <>
            {/* Phase 2 List View */}
            {viewMode === 'list' && (
              <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                <div className="border-b border-gray-100 bg-gray-50 px-6 py-4">
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                    <Search className="h-5 w-5 text-blue-500" />
                    Phase 4: Care Provider Matching Results
                  </h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Click on a match to expand full results
                  </p>
                  <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
                    <span>{allSearchExecutions.length} match{allSearchExecutions.length !== 1 ? 'es' : ''}</span>
                    <span>•</span>
                    <span>{totalSearchCost.toFixed(3)} USD total</span>
                    <span>•</span>
                    <span>{(totalSearchTime / 1000).toFixed(1)}s total</span>
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                  {sortedSearchExecutions.map((searchExec, index) => {
                    const rank = getSearchRank(searchExec.blindCode);
                    const verdict = completedSearchEval?.items?.find(
                      (item) => item.blindCode === searchExec.blindCode
                    )?.verdict;
                    const resultCount = searchExec.searchResults?.webResults?.length || 0;
                    const isWinner = highlightWinner && searchExec.blindCode === searchWinnerBlindCode;
                    const isExpanded = expandedSearchExecutions.has(searchExec.id);

                    return (
                      <div
                        key={searchExec.id}
                        className={clsx(
                          isWinner && 'bg-gradient-to-r from-blue-50 to-cyan-50 border-l-4 border-l-blue-400'
                        )}
                      >
                        {/* Clickable Header */}
                        <button
                          onClick={() => toggleSearchExpanded(searchExec.id)}
                          className="w-full p-6 text-left hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-6">
                            <div className="flex items-start gap-4">
                              <div
                                className={clsx(
                                  'flex h-10 w-10 items-center justify-center rounded-full font-bold',
                                  index === 0
                                    ? 'bg-blue-200 text-blue-800'
                                    : index === 1
                                    ? 'bg-gray-200 text-gray-700'
                                    : index === 2
                                    ? 'bg-orange-200 text-orange-800'
                                    : 'bg-gray-100 text-gray-500'
                                )}
                              >
                                {rank}
                              </div>
                              <div>
                                <div className="flex items-center gap-3">
                                  <ModelBadge model={searchExec.searchModel} />
                                  <span className="text-xs text-gray-400">with criteria from</span>
                                  <ModelBadge model={searchExec.criteriaModel} />
                                  <span className="text-xs text-gray-400 font-mono">
                                    {searchExec.blindCode}
                                  </span>
                                  {isWinner && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                                      <Crown className="h-3 w-3" />
                                      Best
                                    </span>
                                  )}
                                </div>
                                <p className="mt-1 text-xs text-gray-500">
                                  {resultCount} provider{resultCount !== 1 ? 's' : ''} found
                                </p>
                                <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
                                  <span>
                                    <Clock className="inline h-3 w-3 mr-1" />
                                    {((searchExec.latencyMs || 0) / 1000).toFixed(1)}s
                                  </span>
                                  <span>
                                    <DollarSign className="inline h-3 w-3 mr-1" />
                                    {(searchExec.costUsd || 0).toFixed(4)}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-4">
                              {/* Auto Score - always shown for alignment */}
                              <div className="w-28 hidden md:block">
                                <ScoreBar
                                  score={searchExec.autoScore?.combinedScore || 0}
                                  label="Auto Score"
                                />
                              </div>

                              {/* Human Verdict - always shown for alignment */}
                              <div className="w-24 text-right hidden sm:block">
                                <span className="text-xs text-gray-500">Verdict</span>
                                <div className="mt-1">
                                  {verdict ? (
                                    <SearchVerdictDisplay verdict={verdict} />
                                  ) : (
                                    <span className="text-xs text-gray-400">—</span>
                                  )}
                                </div>
                              </div>

                              {/* Expand Icon */}
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-100">
                                {isExpanded ? (
                                  <ChevronUp className="h-4 w-4 text-gray-500" />
                                ) : (
                                  <ChevronDown className="h-4 w-4 text-gray-500" />
                                )}
                              </div>
                            </div>
                          </div>
                        </button>

                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="px-6 pb-6 border-t border-gray-100">
                            {/* Search Summary */}
                            {searchExec.searchResults?.searchSummary && (
                              <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                                <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
                                  <Lightbulb className="h-4 w-4" />
                                  Matching Summary
                                </h4>
                                <p className="text-sm text-blue-800">{searchExec.searchResults.searchSummary}</p>
                              </div>
                            )}

                            {/* Auto Scores Breakdown */}
                            {searchExec.autoScore && (
                              <div className="mt-4">
                                <h4 className="text-sm font-medium text-gray-900 mb-3">Auto Score Breakdown</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                  {searchExec.autoScore.relevanceScore !== undefined && (
                                    <div className="p-3 rounded-lg bg-gray-50">
                                      <ScoreBar score={searchExec.autoScore.relevanceScore} label="Relevance" />
                                    </div>
                                  )}
                                  {searchExec.autoScore.geographicScore !== undefined && (
                                    <div className="p-3 rounded-lg bg-gray-50">
                                      <ScoreBar score={searchExec.autoScore.geographicScore} label="Geographic" />
                                    </div>
                                  )}
                                  {searchExec.autoScore.specialtyMatchScore !== undefined && (
                                    <div className="p-3 rounded-lg bg-gray-50">
                                      <ScoreBar score={searchExec.autoScore.specialtyMatchScore} label="Specialty Match" />
                                    </div>
                                  )}
                                  {searchExec.autoScore.combinedScore !== undefined && (
                                    <div className="p-3 rounded-lg bg-gray-50">
                                      <ScoreBar score={searchExec.autoScore.combinedScore} label="Combined" />
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Full Search Results */}
                            {searchExec.searchResults?.webResults && searchExec.searchResults.webResults.length > 0 && (
                              <div className="mt-4">
                                <h4 className="text-sm font-medium text-gray-900 mb-3">
                                  Matching Results ({searchExec.searchResults.webResults.length})
                                </h4>
                                <div className="space-y-2">
                                  {searchExec.searchResults.webResults.map((result, idx) => (
                                    <div
                                      key={idx}
                                      className="p-3 rounded-lg border border-gray-200 bg-white"
                                    >
                                      <div className="flex items-start justify-between gap-4">
                                        <div className="flex items-start gap-3">
                                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-xs font-bold text-gray-600 shrink-0">
                                            {idx + 1}
                                          </div>
                                          <div>
                                            <p className="font-medium text-gray-900">{result.name}</p>
                                            {result.title && (
                                              <p className="text-sm text-gray-600">{result.title}</p>
                                            )}
                                            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                              {result.specialty && (
                                                <span className="px-2 py-0.5 rounded bg-purple-100 text-purple-700">
                                                  {result.specialty}
                                                </span>
                                              )}
                                              {result.institution && (
                                                <span>{result.institution}</span>
                                              )}
                                              {(result.city || result.canton) && (
                                                <span>
                                                  {[result.city, result.canton].filter(Boolean).join(', ')}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                        {result.matchScore !== undefined && (
                                          <div className="text-right shrink-0">
                                            <span className={clsx(
                                              'text-sm font-bold',
                                              result.matchScore >= 80 ? 'text-green-600' :
                                              result.matchScore >= 60 ? 'text-amber-600' : 'text-gray-500'
                                            )}>
                                              {result.matchScore}%
                                            </span>
                                            <p className="text-xs text-gray-400">match</p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* No Results Message */}
                            {(!searchExec.searchResults?.webResults || searchExec.searchResults.webResults.length === 0) && (
                              <div className="mt-4 p-6 text-center text-gray-500 bg-gray-50 rounded-lg">
                                <Search className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                                <p>No search results available</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Phase 2 Comparison View */}
            {viewMode === 'comparison' && (
              <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                <div className="border-b border-gray-100 bg-gray-50 px-6 py-4">
                  <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                    <Columns className="h-5 w-5 text-blue-500" />
                    Phase 4: Care Provider Matching Comparison
                  </h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Compare matching outputs across key metrics • {allSearchExecutions.length} matches
                  </p>
                </div>

                {/* Comparison Table */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Metric
                        </th>
                        {sortedSearchExecutions.map((searchExec) => {
                          const isWinner = highlightWinner && searchExec.blindCode === searchWinnerBlindCode;
                          return (
                            <th
                              key={searchExec.id}
                              className={clsx(
                                'px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider min-w-[180px]',
                                isWinner ? 'bg-blue-50 text-blue-700' : 'text-gray-500'
                              )}
                            >
                              <div className="flex flex-col items-center gap-1">
                                <ModelBadge model={searchExec.searchModel} />
                                <span className="text-[10px] normal-case text-gray-400">
                                  via {MODEL_DISPLAY[searchExec.criteriaModel]?.name || searchExec.criteriaModel}
                                </span>
                                {isWinner && (
                                  <div className="flex items-center gap-1 text-blue-600">
                                    <Crown className="h-3 w-3" />
                                    <span className="text-[10px]">Best</span>
                                  </div>
                                )}
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {/* Rank Row */}
                      <tr>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Rank</td>
                        {sortedSearchExecutions.map((searchExec) => {
                          const rank = getSearchRank(searchExec.blindCode);
                          const isWinner = highlightWinner && searchExec.blindCode === searchWinnerBlindCode;
                          return (
                            <td
                              key={searchExec.id}
                              className={clsx(
                                'px-6 py-4 text-center',
                                isWinner && 'bg-blue-50'
                              )}
                            >
                              <div
                                className={clsx(
                                  'inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold mx-auto',
                                  rank === 1
                                    ? 'bg-blue-200 text-blue-800'
                                    : rank === 2
                                    ? 'bg-gray-200 text-gray-700'
                                    : rank === 3
                                    ? 'bg-orange-200 text-orange-800'
                                    : 'bg-gray-100 text-gray-500'
                                )}
                              >
                                {rank}
                              </div>
                            </td>
                          );
                        })}
                      </tr>

                      {/* Results Count Row */}
                      <tr>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Results Found</td>
                        {sortedSearchExecutions.map((searchExec) => {
                          const isWinner = highlightWinner && searchExec.blindCode === searchWinnerBlindCode;
                          const resultCount = searchExec.searchResults?.webResults?.length || 0;
                          return (
                            <td
                              key={searchExec.id}
                              className={clsx(
                                'px-6 py-4 text-center',
                                isWinner && 'bg-blue-50'
                              )}
                            >
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 text-gray-700 text-sm font-medium">
                                <Users className="h-3 w-3" />
                                {resultCount}
                              </span>
                            </td>
                          );
                        })}
                      </tr>

                      {/* Auto Score Rows */}
                      <tr>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Combined Score</td>
                        {sortedSearchExecutions.map((searchExec) => {
                          const isWinner = highlightWinner && searchExec.blindCode === searchWinnerBlindCode;
                          const score = searchExec.autoScore?.combinedScore || 0;
                          return (
                            <td
                              key={searchExec.id}
                              className={clsx(
                                'px-6 py-4 text-center',
                                isWinner && 'bg-blue-50'
                              )}
                            >
                              {searchExec.autoScore ? (
                                <div className="flex flex-col items-center gap-1">
                                  <span className={clsx(
                                    'text-lg font-bold',
                                    score >= 80 ? 'text-green-600' : score >= 60 ? 'text-amber-600' : 'text-red-600'
                                  )}>
                                    {Math.round(score)}%
                                  </span>
                                  <div className="w-16 h-1.5 bg-gray-200 rounded-full">
                                    <div
                                      className={clsx(
                                        'h-full rounded-full',
                                        score >= 80 ? 'bg-green-500' : score >= 60 ? 'bg-amber-500' : 'bg-red-500'
                                      )}
                                      style={{ width: `${score}%` }}
                                    />
                                  </div>
                                </div>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      <tr>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Relevance Score</td>
                        {sortedSearchExecutions.map((searchExec) => {
                          const isWinner = highlightWinner && searchExec.blindCode === searchWinnerBlindCode;
                          const score = searchExec.autoScore?.relevanceScore || 0;
                          return (
                            <td
                              key={searchExec.id}
                              className={clsx(
                                'px-6 py-4 text-center',
                                isWinner && 'bg-blue-50'
                              )}
                            >
                              {searchExec.autoScore?.relevanceScore !== undefined ? (
                                <span className={clsx(
                                  'text-sm font-medium',
                                  score >= 80 ? 'text-green-600' : score >= 60 ? 'text-amber-600' : 'text-red-600'
                                )}>
                                  {Math.round(score)}%
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      <tr>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Geographic Score</td>
                        {sortedSearchExecutions.map((searchExec) => {
                          const isWinner = highlightWinner && searchExec.blindCode === searchWinnerBlindCode;
                          const score = searchExec.autoScore?.geographicScore || 0;
                          return (
                            <td
                              key={searchExec.id}
                              className={clsx(
                                'px-6 py-4 text-center',
                                isWinner && 'bg-blue-50'
                              )}
                            >
                              {searchExec.autoScore?.geographicScore !== undefined ? (
                                <span className={clsx(
                                  'text-sm font-medium',
                                  score >= 80 ? 'text-green-600' : score >= 60 ? 'text-amber-600' : 'text-red-600'
                                )}>
                                  {Math.round(score)}%
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      <tr>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Specialty Match</td>
                        {sortedSearchExecutions.map((searchExec) => {
                          const isWinner = highlightWinner && searchExec.blindCode === searchWinnerBlindCode;
                          const score = searchExec.autoScore?.specialtyMatchScore || 0;
                          return (
                            <td
                              key={searchExec.id}
                              className={clsx(
                                'px-6 py-4 text-center',
                                isWinner && 'bg-blue-50'
                              )}
                            >
                              {searchExec.autoScore?.specialtyMatchScore !== undefined ? (
                                <span className={clsx(
                                  'text-sm font-medium',
                                  score >= 80 ? 'text-green-600' : score >= 60 ? 'text-amber-600' : 'text-red-600'
                                )}>
                                  {Math.round(score)}%
                                </span>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      {/* Latency Row */}
                      <tr>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Latency</td>
                        {sortedSearchExecutions.map((searchExec) => {
                          const isWinner = highlightWinner && searchExec.blindCode === searchWinnerBlindCode;
                          const latency = (searchExec.latencyMs || 0) / 1000;
                          const validLatencies = sortedSearchExecutions
                            .map(se => (se.latencyMs || 0) / 1000)
                            .filter(l => l > 0);
                          const minLatency = validLatencies.length > 0 ? Math.min(...validLatencies) : 0;
                          const isFastest = latency === minLatency && latency > 0;
                          return (
                            <td
                              key={searchExec.id}
                              className={clsx(
                                'px-6 py-4 text-center',
                                isWinner && 'bg-blue-50'
                              )}
                            >
                              {searchExec.latencyMs ? (
                                <div className="flex flex-col items-center gap-0.5">
                                  <span className={clsx(
                                    'text-lg font-bold',
                                    isFastest ? 'text-green-600' : 'text-gray-700'
                                  )}>
                                    {latency.toFixed(1)}s
                                  </span>
                                  {isFastest && (
                                    <span className="text-[10px] text-green-600 font-medium">Fastest</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      {/* Cost Row */}
                      <tr>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Cost</td>
                        {sortedSearchExecutions.map((searchExec) => {
                          const isWinner = highlightWinner && searchExec.blindCode === searchWinnerBlindCode;
                          const cost = searchExec.costUsd || 0;
                          const validCosts = sortedSearchExecutions
                            .map(se => se.costUsd || 0)
                            .filter(c => c > 0);
                          const minCost = validCosts.length > 0 ? Math.min(...validCosts) : 0;
                          const isCheapest = cost === minCost && cost > 0;
                          return (
                            <td
                              key={searchExec.id}
                              className={clsx(
                                'px-6 py-4 text-center',
                                isWinner && 'bg-blue-50'
                              )}
                            >
                              {searchExec.costUsd ? (
                                <div className="flex flex-col items-center gap-0.5">
                                  <span className={clsx(
                                    'text-lg font-bold',
                                    isCheapest ? 'text-green-600' : 'text-gray-700'
                                  )}>
                                    ${cost.toFixed(4)}
                                  </span>
                                  {isCheapest && (
                                    <span className="text-[10px] text-green-600 font-medium">Cheapest</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      {/* Human Verdict Row */}
                      <tr>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Human Verdict</td>
                        {sortedSearchExecutions.map((searchExec) => {
                          const isWinner = highlightWinner && searchExec.blindCode === searchWinnerBlindCode;
                          const verdict = completedSearchEval?.items?.find(
                            (item) => item.blindCode === searchExec.blindCode
                          )?.verdict;
                          return (
                            <td
                              key={searchExec.id}
                              className={clsx(
                                'px-6 py-4 text-center',
                                isWinner && 'bg-blue-50'
                              )}
                            >
                              {verdict ? (
                                <SearchVerdictDisplay verdict={verdict} />
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>

                      {/* Top 3 Results Preview */}
                      <tr>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 align-top">Top Results</td>
                        {sortedSearchExecutions.map((searchExec) => {
                          const isWinner = highlightWinner && searchExec.blindCode === searchWinnerBlindCode;
                          const results = searchExec.searchResults?.webResults?.slice(0, 3) || [];
                          return (
                            <td
                              key={searchExec.id}
                              className={clsx(
                                'px-6 py-4 align-top',
                                isWinner && 'bg-blue-50'
                              )}
                            >
                              {results.length > 0 ? (
                                <div className="space-y-1">
                                  {results.map((result, idx) => (
                                    <div
                                      key={idx}
                                      className="text-xs text-gray-600 flex items-start gap-1"
                                    >
                                      <span className="font-medium text-gray-400 shrink-0">#{idx + 1}</span>
                                      <span className="truncate">{result.name}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-gray-400 text-xs">No results</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* Search Evaluation History */}
      {testRun.searchEvaluationSets && testRun.searchEvaluationSets.length > 0 && (() => {
        // Create mapping from search blind code to search model for Phase 4
        const searchBlindCodeToModel = new Map(
          testRun.executions.flatMap((exec) =>
            (exec.searchExecutions || []).map((se) => [se.blindCode, se.searchModel])
          )
        );

        return (
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">Matching Evaluation History</h2>
            <div className="space-y-3">
              {testRun.searchEvaluationSets.map((evaluation) => {
                const bestBlindCode = evaluation.selectedBest || evaluation.rankings[0];
                const bestModel = bestBlindCode ? searchBlindCodeToModel.get(bestBlindCode) : null;

                return (
                  <div
                    key={evaluation.id}
                    className="flex items-start justify-between rounded-lg bg-gray-50 p-4"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{evaluation.evaluator.name}</p>
                      <p className="text-xs text-gray-500">
                        {format(new Date(evaluation.createdAt), 'PPp')}
                      </p>
                      {evaluation.selectionReasoning && (
                        <p className="mt-2 text-sm text-gray-600">{evaluation.selectionReasoning}</p>
                      )}
                    </div>
                    <div className="text-right text-sm">
                      <p className="text-gray-500 mb-2">Best Match</p>
                      {bestModel ? (
                        <ModelBadge model={bestModel} />
                      ) : (
                        <span className="font-mono text-gray-700">{bestBlindCode || '-'}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Phase 5: Quality Assessment Results - List View (Human) */}
      {viewMode === 'list' && qualityAssessmentData && qualityAssessmentData.summary.completedSets > 0 && qualityAssessmentData.modelResults && qualityAssessmentData.modelResults.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <ClipboardCheck className="h-5 w-5 text-green-600" />
              Phase 5: Human Quality Assessment
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                <Users className="h-3 w-3" />
                Human
              </span>
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Model comparison based on human evaluator assessments (6-dimension scoring)
            </p>
            <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
              <span>{qualityAssessmentData.modelResults.length} model{qualityAssessmentData.modelResults.length !== 1 ? 's' : ''} compared</span>
              <span>•</span>
              <span>{qualityAssessmentData.summary.totalAssessments} provider assessment{qualityAssessmentData.summary.totalAssessments !== 1 ? 's' : ''}</span>
              <span>•</span>
              <span>{qualityAssessmentData.summary.goodMatchCount} good match{qualityAssessmentData.summary.goodMatchCount !== 1 ? 'es' : ''}</span>
            </div>
          </div>

          {/* Model Ranking List */}
          <div className="divide-y divide-gray-100">
            {qualityAssessmentData.modelResults.map((modelResult, index) => (
              <div key={modelResult.model} className={clsx(
                'px-6 py-5',
                index === 0 && 'bg-gradient-to-r from-green-50/50 to-emerald-50/50'
              )}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className={clsx(
                      'flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold',
                      index === 0 ? 'bg-green-100 text-green-700' :
                      index === 1 ? 'bg-blue-100 text-blue-700' :
                      index === 2 ? 'bg-amber-100 text-amber-700' :
                      'bg-gray-100 text-gray-600'
                    )}>
                      #{index + 1}
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <ModelBadge model={modelResult.model} />
                        {index === 0 && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                            <Trophy className="h-3 w-3" />
                            Winner
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {modelResult.totalProviders} provider{modelResult.totalProviders !== 1 ? 's' : ''} recommended • {modelResult.goodMatchCount} good match{modelResult.goodMatchCount !== 1 ? 'es' : ''} ({Math.round(modelResult.goodMatchRate * 100)}%)
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className={clsx(
                      'px-4 py-2 rounded-lg text-lg font-bold',
                      modelResult.avgScore >= 12 ? 'bg-green-100 text-green-700' :
                      modelResult.avgScore >= 8 ? 'bg-amber-100 text-amber-700' :
                      'bg-gray-100 text-gray-600'
                    )}>
                      {modelResult.avgScore}/16
                    </div>
                  </div>
                </div>

                {/* Dimension Score Breakdown */}
                <div className="grid grid-cols-6 gap-3">
                  <div className="text-center p-2 bg-white rounded border border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Medical Fit (A)</p>
                    <p className={clsx(
                      'text-sm font-bold',
                      modelResult.avgDimensionScores.medicalFit >= 2 ? 'text-green-600' : 'text-red-600'
                    )}>
                      {modelResult.avgDimensionScores.medicalFit}/3
                    </p>
                  </div>
                  <div className="text-center p-2 bg-white rounded border border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Urgency (B)</p>
                    <p className={clsx(
                      'text-sm font-bold',
                      modelResult.avgDimensionScores.urgencyTiming >= 2 ? 'text-green-600' : 'text-red-600'
                    )}>
                      {modelResult.avgDimensionScores.urgencyTiming}/3
                    </p>
                  </div>
                  <div className="text-center p-2 bg-white rounded border border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Pathway (C)</p>
                    <p className={clsx(
                      'text-sm font-bold',
                      modelResult.avgDimensionScores.therapyPathway >= 2.5 ? 'text-green-600' : 'text-red-600'
                    )}>
                      {modelResult.avgDimensionScores.therapyPathway}/3
                    </p>
                  </div>
                  <div className="text-center p-2 bg-white rounded border border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Complexity (D)</p>
                    <p className="text-sm font-bold text-gray-700">
                      {modelResult.avgDimensionScores.complexityRisk}/2
                    </p>
                  </div>
                  <div className="text-center p-2 bg-white rounded border border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Outcome (E)</p>
                    <p className={clsx(
                      'text-sm font-bold',
                      modelResult.avgDimensionScores.outcomeFit >= 2 ? 'text-green-600' : 'text-red-600'
                    )}>
                      {modelResult.avgDimensionScores.outcomeFit}/3
                    </p>
                  </div>
                  <div className="text-center p-2 bg-white rounded border border-gray-100">
                    <p className="text-xs text-gray-500 mb-1">Patient (F)</p>
                    <p className="text-sm font-bold text-gray-700">
                      {modelResult.avgDimensionScores.patientCentricity}/2
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Phase 5: Quality Assessment Results - Comparison View (Human) */}
      {viewMode === 'comparison' && qualityAssessmentData && qualityAssessmentData.summary.completedSets > 0 && qualityAssessmentData.modelResults && qualityAssessmentData.modelResults.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="border-b border-gray-100 bg-gradient-to-r from-green-50 to-emerald-50 px-6 py-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Columns className="h-5 w-5 text-green-600" />
              Phase 5: Human Quality Assessment Comparison
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                <Users className="h-3 w-3" />
                Human
              </span>
            </h2>
            <p className="text-xs text-gray-500 mt-1">
              Side-by-side comparison of human assessments across models
            </p>
          </div>

          {/* Comparison Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Metric
                  </th>
                  {qualityAssessmentData.modelResults.map((modelResult, index) => {
                    const isWinner = highlightWinner && index === 0;
                    return (
                      <th
                        key={modelResult.model}
                        className={clsx(
                          'px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider min-w-[160px]',
                          isWinner ? 'bg-green-50 text-green-700' : 'text-gray-500'
                        )}
                      >
                        <div className="flex flex-col items-center gap-1">
                          <ModelBadge model={modelResult.model} />
                          {isWinner && (
                            <div className="flex items-center gap-1 text-green-600">
                              <Trophy className="h-3 w-3" />
                              <span className="text-[10px]">Winner</span>
                            </div>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {/* Rank Row */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Rank</td>
                  {qualityAssessmentData.modelResults.map((modelResult, index) => {
                    const isWinner = highlightWinner && index === 0;
                    return (
                      <td
                        key={modelResult.model}
                        className={clsx(
                          'px-6 py-4 text-center',
                          isWinner && 'bg-green-50'
                        )}
                      >
                        <div
                          className={clsx(
                            'inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold mx-auto',
                            index === 0 ? 'bg-green-200 text-green-800' :
                            index === 1 ? 'bg-gray-200 text-gray-700' :
                            index === 2 ? 'bg-orange-200 text-orange-800' :
                            'bg-gray-100 text-gray-500'
                          )}
                        >
                          {index + 1}
                        </div>
                      </td>
                    );
                  })}
                </tr>

                {/* Total Score Row */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Avg Score</td>
                  {qualityAssessmentData.modelResults.map((modelResult, index) => {
                    const isWinner = highlightWinner && index === 0;
                    return (
                      <td
                        key={modelResult.model}
                        className={clsx(
                          'px-6 py-4 text-center',
                          isWinner && 'bg-green-50'
                        )}
                      >
                        <span className={clsx(
                          'text-lg font-bold',
                          modelResult.avgScore >= 12 ? 'text-green-600' :
                          modelResult.avgScore >= 8 ? 'text-amber-600' :
                          'text-gray-600'
                        )}>
                          {modelResult.avgScore}/16
                        </span>
                      </td>
                    );
                  })}
                </tr>

                {/* Providers Row */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Providers</td>
                  {qualityAssessmentData.modelResults.map((modelResult, index) => {
                    const isWinner = highlightWinner && index === 0;
                    return (
                      <td
                        key={modelResult.model}
                        className={clsx(
                          'px-6 py-4 text-center',
                          isWinner && 'bg-green-50'
                        )}
                      >
                        <span className="text-sm font-medium text-gray-700">
                          {modelResult.totalProviders}
                        </span>
                      </td>
                    );
                  })}
                </tr>

                {/* Good Match Rate Row */}
                <tr>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">Good Match Rate</td>
                  {qualityAssessmentData.modelResults.map((modelResult, index) => {
                    const isWinner = highlightWinner && index === 0;
                    const rate = Math.round(modelResult.goodMatchRate * 100);
                    return (
                      <td
                        key={modelResult.model}
                        className={clsx(
                          'px-6 py-4 text-center',
                          isWinner && 'bg-green-50'
                        )}
                      >
                        <div className="flex flex-col items-center gap-1">
                          <span className={clsx(
                            'text-sm font-bold',
                            rate >= 75 ? 'text-green-600' : rate >= 50 ? 'text-amber-600' : 'text-red-600'
                          )}>
                            {rate}%
                          </span>
                          <span className="text-xs text-gray-500">
                            ({modelResult.goodMatchCount}/{modelResult.totalProviders})
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </tr>

                {/* Dimension Scores Header */}
                <tr className="bg-gray-50">
                  <td colSpan={qualityAssessmentData.modelResults.length + 1} className="px-6 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Dimension Scores (Avg)
                  </td>
                </tr>

                {/* Medical Fit */}
                <tr>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">Medical Fit (A)</td>
                  {qualityAssessmentData.modelResults.map((modelResult, index) => {
                    const isWinner = highlightWinner && index === 0;
                    const score = modelResult.avgDimensionScores.medicalFit;
                    return (
                      <td
                        key={modelResult.model}
                        className={clsx(
                          'px-6 py-3 text-center',
                          isWinner && 'bg-green-50'
                        )}
                      >
                        <span className={clsx(
                          'text-sm font-bold',
                          score >= 2 ? 'text-green-600' : 'text-red-600'
                        )}>
                          {score}/3
                        </span>
                      </td>
                    );
                  })}
                </tr>

                {/* Urgency Timing */}
                <tr>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">Urgency (B)</td>
                  {qualityAssessmentData.modelResults.map((modelResult, index) => {
                    const isWinner = highlightWinner && index === 0;
                    const score = modelResult.avgDimensionScores.urgencyTiming;
                    return (
                      <td
                        key={modelResult.model}
                        className={clsx(
                          'px-6 py-3 text-center',
                          isWinner && 'bg-green-50'
                        )}
                      >
                        <span className={clsx(
                          'text-sm font-bold',
                          score >= 2 ? 'text-green-600' : 'text-red-600'
                        )}>
                          {score}/3
                        </span>
                      </td>
                    );
                  })}
                </tr>

                {/* Therapy Pathway */}
                <tr>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">Pathway (C)</td>
                  {qualityAssessmentData.modelResults.map((modelResult, index) => {
                    const isWinner = highlightWinner && index === 0;
                    const score = modelResult.avgDimensionScores.therapyPathway;
                    return (
                      <td
                        key={modelResult.model}
                        className={clsx(
                          'px-6 py-3 text-center',
                          isWinner && 'bg-green-50'
                        )}
                      >
                        <span className={clsx(
                          'text-sm font-bold',
                          score >= 2.5 ? 'text-green-600' : 'text-red-600'
                        )}>
                          {score}/3
                        </span>
                      </td>
                    );
                  })}
                </tr>

                {/* Complexity Risk */}
                <tr>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">Complexity (D)</td>
                  {qualityAssessmentData.modelResults.map((modelResult, index) => {
                    const isWinner = highlightWinner && index === 0;
                    const score = modelResult.avgDimensionScores.complexityRisk;
                    return (
                      <td
                        key={modelResult.model}
                        className={clsx(
                          'px-6 py-3 text-center',
                          isWinner && 'bg-green-50'
                        )}
                      >
                        <span className="text-sm font-bold text-gray-700">
                          {score}/2
                        </span>
                      </td>
                    );
                  })}
                </tr>

                {/* Outcome Fit */}
                <tr>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">Outcome (E)</td>
                  {qualityAssessmentData.modelResults.map((modelResult, index) => {
                    const isWinner = highlightWinner && index === 0;
                    const score = modelResult.avgDimensionScores.outcomeFit;
                    return (
                      <td
                        key={modelResult.model}
                        className={clsx(
                          'px-6 py-3 text-center',
                          isWinner && 'bg-green-50'
                        )}
                      >
                        <span className={clsx(
                          'text-sm font-bold',
                          score >= 2 ? 'text-green-600' : 'text-red-600'
                        )}>
                          {score}/3
                        </span>
                      </td>
                    );
                  })}
                </tr>

                {/* Patient Centricity */}
                <tr>
                  <td className="px-6 py-3 text-sm font-medium text-gray-900">Patient (F)</td>
                  {qualityAssessmentData.modelResults.map((modelResult, index) => {
                    const isWinner = highlightWinner && index === 0;
                    const score = modelResult.avgDimensionScores.patientCentricity;
                    return (
                      <td
                        key={modelResult.model}
                        className={clsx(
                          'px-6 py-3 text-center',
                          isWinner && 'bg-green-50'
                        )}
                      >
                        <span className="text-sm font-bold text-gray-700">
                          {score}/2
                        </span>
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Phase 5: Quality Assessment History (Human) */}
      {qualityAssessmentData && qualityAssessmentData.assessmentSets.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900">
            Human Assessment History
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              <Users className="h-3 w-3" />
              Human
            </span>
          </h2>
          <div className="space-y-3">
            {qualityAssessmentData.assessmentSets.map((assessmentSet) => (
              <div
                key={assessmentSet.id}
                className="flex items-start justify-between rounded-lg bg-gray-50 p-4"
              >
                <div>
                  <p className="font-medium text-gray-900">{assessmentSet.evaluator.name}</p>
                  <p className="text-xs text-gray-500">
                    {assessmentSet.completedAt
                      ? format(new Date(assessmentSet.completedAt), 'PPp')
                      : 'In progress'}
                  </p>
                  {assessmentSet.evaluatorNotes && (
                    <p className="mt-2 text-sm text-gray-600">{assessmentSet.evaluatorNotes}</p>
                  )}
                </div>
                <div className="text-right text-sm">
                  <p className="text-gray-500">Assessments</p>
                  <p className="font-medium text-gray-700">
                    {assessmentSet.assessments.length} provider{assessmentSet.assessments.length !== 1 ? 's' : ''} (top {assessmentSet.topN})
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {assessmentSet.assessments.filter(a => a.isGoodMatch).length} good match{assessmentSet.assessments.filter(a => a.isGoodMatch).length !== 1 ? 'es' : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Quality Assessment Panel - shown after human assessments */}
      {/* Show trigger button when no assessment exists */}
      {testRun?.isRevealed && !aiAssessmentData && !isLoadingAIAssessment && !triggerAIAssessmentMutation.isPending && (
        <div className="rounded-xl border border-indigo-200 bg-white overflow-hidden">
          <div className="px-6 py-8 text-center">
            <Bot className="h-12 w-12 text-indigo-400 mx-auto mb-4" />
            <h2 className="flex items-center justify-center gap-2 text-lg font-semibold text-gray-900 mb-2">
              AI Quality Assessment
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                <Bot className="h-3 w-3" />
                AI
              </span>
            </h2>
            <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
              Run an automated AI Quality Assessment of the matching results using Claude Opus with web search verification. This evaluates each provider on 6 quality dimensions.
            </p>
            <button
              onClick={() => triggerAIAssessmentMutation.mutate()}
              className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
            >
              <Sparkles className="h-5 w-5" />
              Run AI Quality Assessment
            </button>
          </div>
        </div>
      )}

      {/* Show loading state when assessment is running */}
      {(triggerAIAssessmentMutation.isPending || (aiAssessmentData && aiAssessmentData.status === 'RUNNING')) && (
        <div className="rounded-xl border border-indigo-200 bg-white overflow-hidden">
          <div className="px-6 py-8 text-center">
            <Loader2 className="h-12 w-12 text-indigo-600 mx-auto mb-4 animate-spin" />
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Running AI Quality Assessment...</h2>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              Claude Opus is analyzing the matching results and verifying provider claims via web search. This may take a few minutes.
            </p>
          </div>
        </div>
      )}

      {/* Show error state if assessment failed */}
      {aiAssessmentData && aiAssessmentData.status === 'FAILED' && (
        <div className="rounded-xl border border-red-200 bg-white overflow-hidden">
          <div className="px-6 py-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">AI Evaluation Failed</h2>
                <p className="text-sm text-red-600 mb-4">
                  {aiAssessmentData.error || 'An error occurred during the evaluation.'}
                </p>
                <button
                  onClick={() => triggerAIAssessmentMutation.mutate()}
                  disabled={triggerAIAssessmentMutation.isPending}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  <RefreshCw className="h-4 w-4" />
                  Retry Evaluation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show completed AI assessment results - matching Human layout */}
      {aiAssessmentData && aiAssessmentData.status === 'COMPLETED' && (
        <div className="rounded-xl border border-indigo-200 bg-white overflow-hidden">
          {/* Header - matching Human layout */}
          <div className="border-b border-indigo-100 bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                  <Bot className="h-5 w-5 text-indigo-600" />
                  Phase 5: AI Quality Assessment
                  <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                    <Bot className="h-3 w-3" />
                    AI
                  </span>
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  Model comparison based on AI evaluator assessments (6-dimension scoring)
                </p>
                <div className="mt-2 flex items-center gap-4 text-xs text-gray-400">
                  <span>{aiAssessmentData.modelRankings?.length || 0} model{(aiAssessmentData.modelRankings?.length || 0) !== 1 ? 's' : ''} compared</span>
                  <span>•</span>
                  <span>{aiAssessmentData.providerAssessments?.length || 0} provider assessment{(aiAssessmentData.providerAssessments?.length || 0) !== 1 ? 's' : ''}</span>
                  <span>•</span>
                  <span>{aiAssessmentData.providerAssessments?.filter(p => p.isGoodMatch).length || 0} good match{(aiAssessmentData.providerAssessments?.filter(p => p.isGoodMatch).length || 0) !== 1 ? 'es' : ''}</span>
                </div>
              </div>
              <button
                onClick={() => triggerAIAssessmentMutation.mutate()}
                disabled={triggerAIAssessmentMutation.isPending}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-indigo-600 hover:text-indigo-700 hover:bg-indigo-100 rounded-lg transition-colors"
                title="Re-run AI Quality Assessment"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Re-run Assessment
              </button>
            </div>
          </div>

          {/* Model Ranking List - matching Human layout exactly */}
          {aiAssessmentData.modelRankings && aiAssessmentData.modelRankings.length > 0 && (
            <div className="divide-y divide-gray-100">
              {aiAssessmentData.modelRankings.map((ranking, index) => {
                // Calculate avg dimension scores from provider assessments for this model
                const modelProviders = aiAssessmentData.providerAssessments?.filter(
                  pa => pa.recommendedByModels.includes(ranking.model)
                ) || [];
                const avgDimScores = modelProviders.length > 0 ? {
                  medicalFit: Math.round((modelProviders.reduce((sum, p) => sum + p.scores.medicalFit, 0) / modelProviders.length) * 10) / 10,
                  urgencyTiming: Math.round((modelProviders.reduce((sum, p) => sum + p.scores.urgencyTiming, 0) / modelProviders.length) * 10) / 10,
                  pathwayCoherence: Math.round((modelProviders.reduce((sum, p) => sum + p.scores.pathwayCoherence, 0) / modelProviders.length) * 10) / 10,
                  complexityRisk: Math.round((modelProviders.reduce((sum, p) => sum + p.scores.complexityRisk, 0) / modelProviders.length) * 10) / 10,
                  evidenceQuality: Math.round((modelProviders.reduce((sum, p) => sum + p.scores.evidenceQuality, 0) / modelProviders.length) * 10) / 10,
                  patientCentricity: Math.round((modelProviders.reduce((sum, p) => sum + p.scores.patientCentricity, 0) / modelProviders.length) * 10) / 10,
                } : { medicalFit: 0, urgencyTiming: 0, pathwayCoherence: 0, complexityRisk: 0, evidenceQuality: 0, patientCentricity: 0 };

                return (
                  <div key={ranking.model} className={clsx(
                    'px-6 py-5',
                    index === 0 && 'bg-gradient-to-r from-indigo-50/50 to-purple-50/50'
                  )}>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <div className={clsx(
                          'flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold',
                          index === 0 ? 'bg-indigo-100 text-indigo-700' :
                          index === 1 ? 'bg-blue-100 text-blue-700' :
                          index === 2 ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-600'
                        )}>
                          #{index + 1}
                        </div>
                        <div>
                          <div className="flex items-center gap-3">
                            <ModelBadge model={ranking.model} />
                            {index === 0 && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                                <Trophy className="h-3 w-3" />
                                Winner
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            {ranking.totalProviders} provider{ranking.totalProviders !== 1 ? 's' : ''} recommended • {ranking.goodMatchCount} good match{ranking.goodMatchCount !== 1 ? 'es' : ''} ({ranking.goodMatchRate}%)
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className={clsx(
                          'px-4 py-2 rounded-lg text-lg font-bold',
                          ranking.avgScore >= 12 ? 'bg-green-100 text-green-700' :
                          ranking.avgScore >= 8 ? 'bg-amber-100 text-amber-700' :
                          'bg-gray-100 text-gray-600'
                        )}>
                          {ranking.avgScore}/16
                        </div>
                      </div>
                    </div>

                    {/* Dimension Score Breakdown - matching Human layout exactly */}
                    <div className="grid grid-cols-6 gap-3">
                      <div className="text-center p-2 bg-white rounded border border-gray-100">
                        <p className="text-xs text-gray-500 mb-1">Medical Fit (A)</p>
                        <p className={clsx(
                          'text-sm font-bold',
                          avgDimScores.medicalFit >= 2 ? 'text-green-600' : 'text-red-600'
                        )}>
                          {avgDimScores.medicalFit}/3
                        </p>
                      </div>
                      <div className="text-center p-2 bg-white rounded border border-gray-100">
                        <p className="text-xs text-gray-500 mb-1">Urgency (B)</p>
                        <p className={clsx(
                          'text-sm font-bold',
                          avgDimScores.urgencyTiming >= 2 ? 'text-green-600' : 'text-red-600'
                        )}>
                          {avgDimScores.urgencyTiming}/3
                        </p>
                      </div>
                      <div className="text-center p-2 bg-white rounded border border-gray-100">
                        <p className="text-xs text-gray-500 mb-1">Pathway (C)</p>
                        <p className={clsx(
                          'text-sm font-bold',
                          avgDimScores.pathwayCoherence >= 2 ? 'text-green-600' : 'text-red-600'
                        )}>
                          {avgDimScores.pathwayCoherence}/3
                        </p>
                      </div>
                      <div className="text-center p-2 bg-white rounded border border-gray-100">
                        <p className="text-xs text-gray-500 mb-1">Complexity (D)</p>
                        <p className="text-sm font-bold text-gray-700">
                          {avgDimScores.complexityRisk}/2
                        </p>
                      </div>
                      <div className="text-center p-2 bg-white rounded border border-gray-100">
                        <p className="text-xs text-gray-500 mb-1">Evidence (E)</p>
                        <p className={clsx(
                          'text-sm font-bold',
                          avgDimScores.evidenceQuality >= 2 ? 'text-green-600' : 'text-red-600'
                        )}>
                          {avgDimScores.evidenceQuality}/3
                        </p>
                      </div>
                      <div className="text-center p-2 bg-white rounded border border-gray-100">
                        <p className="text-xs text-gray-500 mb-1">Patient (F)</p>
                        <p className="text-sm font-bold text-gray-700">
                          {avgDimScores.patientCentricity}/2
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* AI-Specific Additional Information */}
          <div className="border-t border-indigo-100">
            {/* Disagreement Alert */}
            {aiAssessmentData.disagreement.hasDisagreement && aiAssessmentData.disagreement.flags && (
              <div className="px-6 py-3 bg-amber-50 border-b border-amber-200">
                <div className="flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    {aiAssessmentData.disagreement.flags.overallAlignment === 'significant_differences'
                      ? 'Significant disagreement with human assessments'
                      : 'Minor differences with human assessments'}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {aiAssessmentData.disagreement.flags.significantDisagreements.map((d, i) => (
                    <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 rounded text-xs text-amber-700">
                      {d.dimension}: AI {d.aiScore} vs Human {d.humanAvgScore}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Provider Assessments Detail */}
            {aiAssessmentData.providerAssessments && aiAssessmentData.providerAssessments.length > 0 && (
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Stethoscope className="h-4 w-4 text-indigo-600" />
                  Provider Assessments Detail ({aiAssessmentData.providerAssessments.length})
                </h3>
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {aiAssessmentData.providerAssessments.map((pa) => (
                    <div key={pa.id} className={clsx(
                      'p-3 rounded-lg border',
                      pa.isGoodMatch ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                    )}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{pa.providerName}</span>
                            {pa.isGoodMatch && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                <CheckCircle className="h-3 w-3" />
                                Good Match
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500">
                            {pa.providerType} • {pa.providerLocation} • {pa.providerSpecialty || 'Unknown specialty'}
                          </p>
                          <div className="mt-1 flex items-center gap-1 flex-wrap">
                            {pa.recommendedByModels.map(model => (
                              <span key={model} className="text-xs px-1.5 py-0.5 bg-gray-200 rounded">
                                {MODEL_DISPLAY[model]?.name || model}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className={clsx(
                          'px-2 py-1 rounded font-bold text-sm',
                          pa.totalScore >= 12 ? 'bg-green-100 text-green-700' :
                          pa.totalScore >= 8 ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        )}>
                          {pa.totalScore}/16
                        </div>
                      </div>
                      <div className="mt-2 grid grid-cols-6 gap-1 text-xs">
                        <div className="text-center">
                          <span className="text-gray-500">A</span>
                          <span className={clsx('ml-1 font-medium', pa.scores.medicalFit >= 2 ? 'text-green-600' : 'text-red-600')}>
                            {pa.scores.medicalFit}
                          </span>
                        </div>
                        <div className="text-center">
                          <span className="text-gray-500">B</span>
                          <span className={clsx('ml-1 font-medium', pa.scores.urgencyTiming >= 2 ? 'text-green-600' : 'text-red-600')}>
                            {pa.scores.urgencyTiming}
                          </span>
                        </div>
                        <div className="text-center">
                          <span className="text-gray-500">C</span>
                          <span className={clsx('ml-1 font-medium', pa.scores.pathwayCoherence >= 2 ? 'text-green-600' : 'text-red-600')}>
                            {pa.scores.pathwayCoherence}
                          </span>
                        </div>
                        <div className="text-center">
                          <span className="text-gray-500">D</span>
                          <span className="ml-1 font-medium text-gray-700">{pa.scores.complexityRisk}</span>
                        </div>
                        <div className="text-center">
                          <span className="text-gray-500">E</span>
                          <span className={clsx('ml-1 font-medium', pa.scores.evidenceQuality >= 2 ? 'text-green-600' : 'text-red-600')}>
                            {pa.scores.evidenceQuality}
                          </span>
                        </div>
                        <div className="text-center">
                          <span className="text-gray-500">F</span>
                          <span className="ml-1 font-medium text-gray-700">{pa.scores.patientCentricity}</span>
                        </div>
                      </div>
                      {pa.overallAssessment && (
                        <p className="mt-2 text-xs text-gray-600 line-clamp-2">{pa.overallAssessment}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Overall Justification */}
            {aiAssessmentData.overall.justification && (
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Overall Assessment</h3>
                <p className="text-sm text-gray-600">{aiAssessmentData.overall.justification}</p>
              </div>
            )}

            {/* Critical Findings */}
            {aiAssessmentData.criticalFindings && aiAssessmentData.criticalFindings.length > 0 && (
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-red-700 mb-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  Critical Findings ({aiAssessmentData.criticalFindings.length})
                </h3>
                <div className="space-y-2">
                  {aiAssessmentData.criticalFindings.map((finding, i) => (
                    <div key={i} className="p-3 bg-red-50 rounded border border-red-200">
                      <div className="font-medium text-red-800 text-sm">{finding.finding}</div>
                      <div className="text-xs text-red-600 mt-1">{finding.whyItMatters}</div>
                      {finding.recommendedFix && (
                        <div className="text-xs text-gray-600 mt-1">
                          <span className="font-medium">Fix:</span> {finding.recommendedFix}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Unverified Risks */}
            {aiAssessmentData.unverifiedRisks && aiAssessmentData.unverifiedRisks.length > 0 && (
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-amber-700 mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Unverified Risks ({aiAssessmentData.unverifiedRisks.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {aiAssessmentData.unverifiedRisks.map((risk, i) => (
                    <div key={i} className={clsx(
                      'px-3 py-2 rounded border text-xs',
                      risk.impactIfWrong === 'high' ? 'bg-red-50 border-red-200 text-red-700' :
                      risk.impactIfWrong === 'medium' ? 'bg-amber-50 border-amber-200 text-amber-700' :
                      'bg-gray-50 border-gray-200 text-gray-700'
                    )}>
                      <div className="font-medium">{risk.risk}</div>
                      <div className="text-xs opacity-75 mt-1">{risk.whatToVerifyNext}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Evaluation Confidence */}
            {aiAssessmentData.evaluation.confidence && (
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Evaluation Confidence</h3>
                <div className="flex items-center gap-3">
                  <span className={clsx(
                    'px-3 py-1 rounded-full text-sm font-medium',
                    aiAssessmentData.evaluation.confidence === 'high' ? 'bg-green-100 text-green-700' :
                    aiAssessmentData.evaluation.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  )}>
                    {aiAssessmentData.evaluation.confidence.charAt(0).toUpperCase() + aiAssessmentData.evaluation.confidence.slice(1)}
                  </span>
                  {aiAssessmentData.evaluation.confidenceReasons && aiAssessmentData.evaluation.confidenceReasons.length > 0 && (
                    <span className="text-xs text-gray-500">
                      {aiAssessmentData.evaluation.confidenceReasons.join(', ')}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Metrics Footer */}
            <div className="px-6 py-3 bg-gray-50 flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center gap-4">
                <span>Model: {aiAssessmentData.model}</span>
                <span>•</span>
                <span>Prompt v{aiAssessmentData.promptVersion}</span>
                {aiAssessmentData.metrics.latencyMs && (
                  <>
                    <span>•</span>
                    <span>Latency: {(aiAssessmentData.metrics.latencyMs / 1000).toFixed(1)}s</span>
                  </>
                )}
                {aiAssessmentData.metrics.costUsd && (
                  <>
                    <span>•</span>
                    <span>Cost: ${aiAssessmentData.metrics.costUsd.toFixed(4)}</span>
                  </>
                )}
              </div>
              <div className="font-mono text-xs text-gray-400">
                Hash: {aiAssessmentData.promptHash}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
